<template>
  <PeopleAndGroupsTemplate
    tab="groups"
    :selectedLength="table.selected.length"
    :clearSelected="clearSelected"
    @update-search="handleSearchValueChange"
  >
    <template #selected-count>
      {{ table.selected.length }} Group{{ table.selected.length == 1 ? "" : "s" }}
      Selected
    </template>
    <template #actions>
      <Button text small @click="displayInformGroupForm" v-if="canManageInformGroups"
        >New Dynamic Data Group</Button
      >

      <Button
        text
        small
        @click="loadComplexGroupForm"
        v-if="canCreateGroup && permissions.includes('rules:create:rule') && !isMobile"
        >New Complex Group</Button
      >

      <Button
        v-if="permissions.includes('vue:read:communicationwizard') && !isMobile"
        :disabled="!table.selected.length"
        text
        small
        @click="loadCommunicationWizard"
        >Send message</Button
      >
      <Button
        v-if="permissions.includes('groups:delete:group')"
        :disabled="!table.selected.length"
        text
        small
        @click="dialogDeleteMessage = true"
        >Delete selected groups</Button
      >
      <div class="full-width divider mt-2 mb-3"></div>
      <Button v-if="canCreateGroup" text small @click="showCSVWidget = true"
        >Create group via CSV</Button
      >
    </template>
    <template #search-row-actions>
      <Button v-if="canCreateGroup" iconLeft="mdi-plus" @click="displayGroupTypeSelector()"
        >New Group</Button
      >
    </template>
    <template #data-table>
      <GroupsDataTable
        :selected="table.selected"
        @update:selected="table.selected = $event"
        :columns="groupColumns"
        :groups="visibleGroups"
        :total="table.total"
        :options="table.options"
        @update:options="table.options = $event"
        :itemsPerPage="table.itemsPerPage"
        :page="table.page"
        @update:page="table.page = $event"
        :loading="table.loading"
        :footerProps="table.footerProps"
        :clickableRows="canOpenGroupDrawer"
        :clientId="clientId"
        @row-click="editGroup"
        @checkbox-click="toggleGroupSelected"
        @qr-click="loadQrCode"
      />
    </template>
    <template #overlay>
      <div>
        <!-- Dialogs -->
        <v-navigation-drawer v-model="showGroupTypeSelector" temporary fixed right width="500">
          <GroupTypeSelector
            v-if="showGroupTypeSelector"
            source="groups"
            :version="3"
            :clientId="clientId"
            @close="closeGroupCreator"
            @get-groups="getGroups(true)"
          ></GroupTypeSelector>
        </v-navigation-drawer>

        <NewInformGroupDialog
          :show="showInformGroupDialog"
          @update:show="showInformGroupDialog = $event"
          :clientId="clientId"
          @create="handleInformGroupCreate"
          @close="requestCloseInformGroupDialog"
        />

        <Modal
          :value="showInformGroupCancelConfirm"
          @input="showInformGroupCancelConfirm = $event"
          max-width="444px"
          :show-close-button="false"
        >
          <template #header>
            <Typography variant="h-5">Are you sure you want to cancel?</Typography>
          </template>
          <Typography variant="body-1" class="text-left black--text pt-4">
            You will lose your unsaved changes.
          </Typography>
          <div class="d-flex justify-end pt-4">
            <Button text @click="showInformGroupCancelConfirm = false">Nevermind</Button>
            <Button text @click="confirmCloseInformGroupDialog">Yes, Cancel</Button>
          </div>
        </Modal>

        <EditInformGroupDialog
          :show="showEditInformGroupDialog"
          @update:show="showEditInformGroupDialog = $event"
          :clientId="clientId"
          :groupData="editingInformGroup || {}"
          @update="handleInformGroupUpdate"
          @close="requestCloseEditInformGroupDialog"
        />

        <Modal
          :value="showEditInformGroupCancelConfirm"
          @input="showEditInformGroupCancelConfirm = $event"
          max-width="444px"
          :show-close-button="false"
        >
          <template #header>
            <Typography variant="h-5">Are you sure you want to cancel?</Typography>
          </template>
          <Typography variant="body-1" class="text-left black--text pt-4">
            You will lose your unsaved changes.
          </Typography>
          <div class="d-flex justify-end pt-4">
            <Button text @click="showEditInformGroupCancelConfirm = false">Nevermind</Button>
            <Button text @click="confirmCloseEditInformGroupDialog">Yes, Cancel</Button>
          </div>
        </Modal>

        <QRViewer
          v-if="qr.dialog"
          :groupId="qr.groupId"
          :clientId="qr.clientId"
          @close="(qr.dialog = false), (qr.groupId = null), (qr.clientId = null), (qr.error = null)"
        />
        <NewComplexGroupDialog
          :show="complexGroupForm.dialogCreator"
          @update:show="complexGroupForm.dialogCreator = $event"
          :hasSubClients="hasSubClients"
          :includeSubClients="complexGroup.includeSubClients"
          @update:includeSubClients="complexGroup.includeSubClients = $event"
          :ruleBuilderTrigger="complexGroupForm.ruleBuilderTrigger"
          :rules="complexGroup.rules"
          :rulesValid="validateRules"
          :metrics="metrics"
          :modules="modules"
          :estimatedParticipants="complexGroup.estimatedParticipants"
          :loadingEstimatedParticipants="complexGroup.loadingEstimatedParticipants"
          :disableEstimatedParticipants="complexGroup.disableEstimatedParticipants"
          :clientId="clientId"
          :readOnly="!complexGroup.showSaveButton"
          @create="handleComplexGroupCreate"
          @close="resetComplexGroupForm"
          @close-rule-builder="closeRuleBuilder"
          @refresh-estimate="refreshComplexGroupEstimate"
          @update-rules="onRulesValueChanged"
          @update-name="onNameValueChanged"
          @update-description="onDescriptionValueChanged"
        />
        <!-- Drawer used for viewing an existing group -->
        <EditGroupDrawer
          :show="showEditGroup"
          @update:show="showEditGroup = $event"
          :stateless="!!qr.dialog"
          :groupData="editingGroup"
          :showGroupAssignmentPage="groupEditForm.showGroupAssignmentPage"
          :columns="groupEditForm.columns"
          :isWhistleClient="hasPermissionToManageClients()"
          :showQrCodeButton="showQrCode(editingGroup.groupType, editingGroup.clientId)"
          :userSearch="userTable.debounceSearch"
          :readonly="readonlyGroupDrawer"
          :canEditInformGroup="canManageInformGroups"
          @update="handleEditGroupUpdate"
          @close="clearEditGroupForm"
          @load-qr-code="loadQrCode"
          @load-user-assignment="loadUserGroupAssignmentPage"
          @refresh-group="({ groupId, clientId }) => refreshGroup(groupId, clientId)"
          @refresh-inform-group="handleRefreshInformGroup"
          @edit-inform-group="openEditInformGroupDialog"
          @delete-assignment="deleteGroupAssignment"
          @back-from-user-assignment="clearUserGroupForm"
          @update:userSearch="userTable.debounceSearch = $event"
          @add-user-to-group="addUserToGroupAssignment"
          @delete-group="deleteGroup"
          @download-group="downloadGroup"
        />
        <!-- Confirm delete dialog -->
        <DeleteGroupsDialog
          :show="dialogDeleteMessage"
          @update:show="dialogDeleteMessage = $event"
          @delete="deleteGroups"
        />
        <!-- Delete error dialog for programs -->
        <DeleteGroupsErrorDialog
          :show="dialogDeleteErrorMessage"
          @update:show="dialogDeleteErrorMessage = $event"
          :errors="dialogDeleteErrorArray"
          :groups="visibleGroups"
        />
        <!-- CSV Widget -->
        <v-navigation-drawer v-model="showCSVWidget" temporary fixed right width="500">
          <GroupCSVWidget
            v-if="showCSVWidget"
            :clientId="clientId"
            @close="showCSVWidget = false"
            @get-groups="getGroups(true)"
          ></GroupCSVWidget>
        </v-navigation-drawer>

        <!-- Dialog used for displaying loading message. -->
        <LoadingDialog
          :showDialog="showLoadingDialog"
          :header="loadingHeaderMessage"
          :line1="loadingMessageLine1"
          :line2="loadingMessageLine2"
        ></LoadingDialog>
      </div>
    </template>
  </PeopleAndGroupsTemplate>
</template>

<script>
import GroupService from "@/services/GroupService";
import RulesService from "@/services/RulesService";
import ProgramService from "@/services/ProgramService";
import LearningService from "@/services/LearningService";
import UserService from "@/services/UserService";
import ClientService from "@/services/ClientServiceV2";
import informTenantMixin from "@/mixins/informTenantMixin";
import csvGenerationMixin from "@/mixins/csvGenerationMixin";

import LoadingDialog from "@/components/loadingDialog.vue";
import PeopleAndGroupsTemplate from "@/components/people-and-groups/PeopleAndGroupsTemplate.vue";
import GroupTypeSelector from "@/components/groups/GroupTypeSelector.vue";
import GroupCSVWidget from "@/components/groups/GroupCSVWidget.vue";
import QRViewer from "@/components/QR.vue";
import Button from "@/components/generic/Button.vue";
import Modal from "@/components/generic/Modal.vue";
import Typography from "@/components/generic/Typography.vue";
import NewComplexGroupDialog from "@/components/groups/NewComplexGroupDialog.vue";
import NewInformGroupDialog from "@/components/groups/NewInformGroupDialog.vue";
import EditInformGroupDialog from "@/components/groups/EditInformGroupDialog.vue";
import EditGroupDrawer from "@/components/groups/EditGroupDrawer.vue";
import DeleteGroupsDialog from "@/components/groups/DeleteGroupsDialog.vue";
import DeleteGroupsErrorDialog from "@/components/groups/DeleteGroupsErrorDialog.vue";
import GroupsDataTable from "@/components/groups/GroupsDataTable.vue";

import { debounce } from "@/shared_data/functions";
import { mapState, mapGetters } from "vuex";

export default {
  name: "Groups",
  title: "Groups",
  mixins: [informTenantMixin, csvGenerationMixin],
  components: {
    LoadingDialog,
    PeopleAndGroupsTemplate,
    GroupTypeSelector,
    GroupCSVWidget,
    QRViewer,
    Button,
    Modal,
    Typography,
    NewComplexGroupDialog,
    NewInformGroupDialog,
    EditInformGroupDialog,
    EditGroupDrawer,
    DeleteGroupsDialog,
    DeleteGroupsErrorDialog,
    GroupsDataTable,
  },
  data() {
    return {
      clientService: new ClientService(),

      showLoadingDialog: false,
      loadingHeaderMessage: null,
      loadingMessageLine1: null,
      loadingMessageLine2: null,
      loadingTable: true,
      showGroupTypeSelector: false,
      showCreateSimpleGroup: false,
      showCreateQrGroup: false,
      hasSubClients: false,
      // selected: [],
      // groups: [],
      loadingUsers: false,
      users: [],
      // allUsers: [],
      // allGroups: [],
      // groupTableSearch: null,
      columns: [
        {
          text: "Group ID",
          align: "start",
          sortable: true,
          value: "groupId",
          width: "10%",
        },
        { text: "Group", value: "groupDisplayName", width: "45%" },
        { text: "Group Type", value: "groupType", width: "15%" },
        // { text: "Rules", value: "enrollmentRules", width: "25%" },
        {
          text: "Participants",
          value: "numInGroup",
          width: "15%",
          // align: "end",
          sortable: false,
        },
        { text: "", value: "qr", width: "15%" },
      ],

      showCreateListGroup: false,
      createListGroupSuccess: false,
      listGroupResultsSuccess: [],
      listGroupResultsErrors: [],
      showEditGroup: false,
      showGroupAssignmentUsers: false,
      file: null,
      groupEditForm: {
        valid: false,
        loadingTable: false,
        tablePage: 1,
        columns: [
          { text: "", value: "delete" },
          { text: "Name", value: "displayName", width: "40%" },
          { text: "Email / Phone", value: "businessEmail", width: "40%" },
        ],

        loadingUserTable: false,
        showGroupAssignmentPage: false,
      },
      editingGroup: {
        clientId: null,
        groupId: null,
        groupName: null,
        groupDisplayName: null,
        groupType: null,
        enrollmentRules: null,
        rules: null,
        ruleName: null,
        ruleDescription: null,
        hierarchyRules: null,
        creatorName: null,
        GroupAssignments: [],
        loadingUsers: false,
        loadingGroupRefresh: false,
        assignedUsers: [],
        userTable: {
          itemsPerPage: 10,
          page: 1,
          // Used if the user ends up mashing the table page button, then we're waiting on multiple responses so we know which page to actually store the data for
          awaitingPage: 1,
          loading: false,
          users: [],
          nextPage: [],
          total: 0,
          selected: [],
          options: {},
          footerProps: {
            "items-per-page-options": [10, 25, 50, 100],
          },
        },
      },
      complexGroupForm: {
        valid: false,
        dialogRuleBuilder: false,
        ruleBuilderTrigger: false,
        dialogCreator: false,
        optimizePerformance: false,
      },
      showInformGroupDialog: false,
      showInformGroupCancelConfirm: false,
      showEditInformGroupDialog: false,
      showEditInformGroupCancelConfirm: false,
      editingInformGroup: null,
      complexGroup: {
        clientId: null,
        groupName: null,
        groupDisplayName: null,
        rules: null,
        ruleName: null,
        ruleDescription: null,
        estimatedParticipants: null,
        loadingEstimatedParticipants: false,
        disableEstimatedParticipants: false,
        showSaveButton: true,
        includeSubClients: false,
        estimatedParticipantsResponseId: null,
      },
      ruleGroups: [],
      metrics: [],
      modules: [],
      dialogDeleteMessage: false,
      dialogDeleteErrorMessage: false,
      dialogDeleteErrorArray: [],
      table: {
        itemsPerPage: 10,
        page: 1,
        // Used if the user ends up mashing the table page button, then we're waiting on multiple responses so we know which page to actually store the data for
        awaitingPage: 1,
        search: null,
        debounceSearch: null,
        loading: false,
        groups: [],
        nextPage: [],
        total: 0,
        selected: [],
        options: {},
        footerProps: {
          "items-per-page-options": [10, 25, 50, 100],
        },
        latestFilter: null,
        latestSort: null,
      },
      userTable: {
        search: null,
        debounceSearch: null,
      },
      showCSVWidget: false,
      qr: {
        url: null,
        dialog: false,
        error: null,
        groupId: null,
        clientId: null,
      },
    };
  },

  created() {
    if (this.canCreateGroup) {
      this.getLearningModules();
      this.getMetrics();

      this.clientService
        .getSubClientsV3(this.clientId)
        .then((response) => {
          this.hasSubClients = response?.result?.filter((clientId) => clientId !== 0)?.length > 0;
        })
        .catch((error) => {
          console.log("Error getting subclients", error.response || error);
        });
    }
  },
  mounted() {
    //Move the fresh works help widget
    var freshworks = document.querySelector("#launcher-frame");
    if (freshworks) {
      freshworks.style.right = "-55px";
      freshworks.style["max-width"] = freshworks.style["min-width"] = "90px";
    }
  },
  unmounted() {
    var freshworks = document.querySelector("#launcher-frame");
    if (freshworks) {
      freshworks.style.right = "22px";
    }
  },
  // beforeUnmount() {
  //   //Clears the search in case they go to a page without search
  //   this.$store.dispatch("setSearchArray", []);
  //   this.unwatch();
  // },
  methods: {
    async getGroups(reset = false, groupId = null) {
      try {
        this.table.loading = true;
        if (reset) {
          this.table.options.page = 1;
        }
        // Used if the user ends up mashing the table page button, then we're waiting on multiple responses so we know which page to actually store the data for
        let options = this.table.options;
        let page = this.table.options.page;
        let search = this.table.search ? this.table.search.replace(/'/g, "\\'") : null;

        let filter = `status != 'Deleted' && clientId=${this.clientId}`;
        if (groupId) filter += ` && groupId = ${groupId}`;
        if (search) {
          // If search we need to search for every field that's in the table
          filter += ` && (groupDisplayName.like('%${search}%') || groupType.like('%${search}%') || groupId = '${search}') `;
        }
        // if first page, fetch double
        let limit = groupId ? 1 : options.itemsPerPage; //page == 1 ? options.itemsPerPage * 2 : options.itemsPerPage;
        let offset = reset || groupId ? 0 : (page - 1) * limit;
        var sort = "groupId DESC";
        if (options.sortBy && options.sortBy.length) {
          let col = options.sortBy;
          let type =
            options.sortDesc && options.sortDesc.length && options.sortDesc[0] === true
              ? "DESC"
              : "ASC";
          // if (col == "description") col = "startDate";
          sort = `${col} ${type}`;
        }
        // let extract =
        //   "clientId,groupId,groupDisplayName,groupType,enrollmentRules";
        this.table.latestSort = sort;
        this.table.latestFilter = filter;
        let response = await GroupService.getGroupsV2({
          filter: filter,
          limit: limit,
          offset: offset,
          sort: sort,
          // extract,
          expand: "null",
          count: 1,
        });

        if (filter != this.table.latestFilter || sort != this.table.latestSort) {
          console.log("Ignoring response");
          return;
        }

        console.log("Group response ", response);
        let rows = response.result.rows;

        if (!groupId) this.table.total = response.result.count;

        // We instantiate the users array with an empty array to match the length of the results
        if (reset) this.table.groups = new Array(this.table.total).fill(undefined);

        //Prune the returned users for the data we need for the table
        rows = rows.map(function (group) {
          group.displayName = group.name = group.groupDisplayName;

          group.id = group.groupId;

          // group.numUsers = (group.GroupAssignments || []).length;
          group.GroupAssignments = [];
          return group;
        });

        // If at the beginning we just grab the first results and then append the rest of the empty array
        // If not the beginning, we append the existing fetched array, then the new results, and anything after
        // this.table.users = rows;
        if (groupId !== null) {
          let existingGroup = this.table.groups.findIndex((x) => x.groupId == groupId);
          if (existingGroup != -1) {
            this.$set(this.table.groups, existingGroup, rows[0]);
            // this.table.groups[existingGroup] = rows[0];
          } else {
            this.table.groups = this.table.groups.concat(rows);
          }
        } else {
          if (offset == 0) this.table.groups = rows.concat(this.table.groups.slice(rows.length));
          else
            this.table.groups = this.table.groups
              .slice(0, offset)
              .concat(rows, this.table.groups.slice(offset + rows.length));
        }
        if (reset) this.selected = [];
      } catch (err) {
        console.log("error getting groups ", err);
      } finally {
        this.table.loading = false;
      }
    },
    displayGroupTypeSelector() {
      this.showGroupTypeSelector = true;
      this.showCreateQrGroup = false;
      this.showCreateSimpleGroup = false;
    },
    displayCreateSimpleGroupForm() {
      this.showCreateSimpleGroup = true;
      this.showCreateQrGroup = false;
      this.showGroupTypeSelector = false;
    },
    displayCreateQrGroupForm() {
      this.showCreateQrGroup = true;
      this.showCreateSimpleGroup = false;
      this.showGroupTypeSelector = false;
    },
    displayInformGroupForm() {
      this.showInformGroupDialog = true;
    },
    requestCloseInformGroupDialog() {
      this.showInformGroupCancelConfirm = true;
    },
    confirmCloseInformGroupDialog() {
      this.showInformGroupCancelConfirm = false;
      this.showInformGroupDialog = false;
    },
    openEditInformGroupDialog() {
      this.editingInformGroup = { ...this.editingGroup };
      this.showEditInformGroupDialog = true;
    },
    closeEditInformGroupDialog() {
      this.showEditInformGroupDialog = false;
      this.showEditInformGroupCancelConfirm = false;
      this.editingInformGroup = null;
    },
    requestCloseEditInformGroupDialog() {
      this.showEditInformGroupCancelConfirm = true;
    },
    confirmCloseEditInformGroupDialog() {
      this.closeEditInformGroupDialog();
    },
    async handleInformGroupUpdate({ groupId, groupName, groupRuleQuery, userIdPath }) {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Updating your dynamic data group";
      this.loadingMessageLine1 = "This should just take a few seconds!";
      this.loadingMessageLine2 = "";

      try {
        await GroupService.updateInformGroupV3({
          groupRuleId: groupId,
          groupDisplayName: groupName,
          query: groupRuleQuery,
          userIdPath,
        });
        await this.refreshInformGroup(groupId);
        this.closeEditInformGroupDialog();
        await this.getGroups(false);
      } catch (error) {
        console.log("Error updating dynamic data group", error?.response || error);
        this.$store.dispatch("showError", {
          message: "Failed to update dynamic data group. Please try again later.",
        });
      } finally {
        this.showLoadingDialog = false;
      }
    },
    async handleInformGroupCreate({ groupName, groupRuleQuery, userIdPath }) {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Creating your dynamic data group";
      this.loadingMessageLine1 = "This should just take a few seconds!";
      this.loadingMessageLine2 = "";

      try {
        const response = await GroupService.createInformGroupV3({
          groupDisplayName: groupName,
          query: groupRuleQuery,
          userIdPath,
          clientId: this.clientId,
        });
        console.log("Dynamic data group created", response);
        await this.refreshInformGroup(response.result.groupId);
        this.showInformGroupDialog = false;
        this.showInformGroupCancelConfirm = false;
        await this.getGroups(true);
      } catch (error) {
        console.log("Error creating dynamic data group", error?.response || error);
        this.$store.dispatch("showError", {
          message: "Failed to create dynamic data group. Please try again later.",
        });
      } finally {
        this.showLoadingDialog = false;
      }
    },
    getMetrics() {
      ProgramService.getMetrics()
        .then((response) => {
          console.log("Metrics: ", response);
          var metrics = response
            .filter(
              (metric) =>
                metric.status !== "Deleted" &&
                (metric.clientId == this.clientId || metric.clientId == 0)
            )
            .map((x) => {
              var obj = x;
              obj.id = x.metricId.toString();
              obj.context = "MetricDefinitions";
              obj.contextResourceId = x.metricId.toString();
              obj.contextColumn = "metricValue";
              obj.label = x.metricName;
              obj.dataType = x.dataType;
              obj.showNumberOptions = x.calculateProgress && x.dataType !== "BOOLEAN";
              return obj;
            });
          this.metrics = metrics;
        })
        .catch((error) => {
          console.log("Error!", error);
        });
    },
    getLearningModules() {
      LearningService.getModulesAdmin("learning", this.clientId)
        .then((response) => {
          console.log("Learning Modules: ", response);
          var modules = response.modules
            .filter((module) => module.status !== "Deleted" && module.clientId == this.clientId)
            .map((x) => {
              var obj = x;
              obj.id = x.learningModuleId.toString();
              obj.context = "LearningModules";
              obj.contextResourceId = x.learningModuleId.toString();
              obj.contextColumn = "metricValue";
              obj.label = "Module Progress: " + x.displayName.substring(0, 30);
              obj.dataType = "INTEGER";
              obj.showNumberOptions = true;
              return obj;
            });
          //Adds header because this array will be used to select metrics for a payoutRule
          this.modules = modules;
        })
        .catch((error) => {
          console.log("Error!", error);
        });
    },
    clearListGroupForm() {
      this.file = null;
      this.showCreateListGroup = false;
      this.showLoadingDialog = false;
      this.createListGroupSuccess = false;
      this.listGroupResultsErrors = [];
      this.listGroupResultsSuccess = [];
    },
    async getGroupAssignments(page, reset = false) {
      console.log("Reset ? ", reset);
      try {
        if (reset) page = 0;
        if (page == 0) this.editingGroup.loadingUsers = true;

        let filter = `status != 'Deleted' && groupId=${this.editingGroup.groupId}`;

        // This array is used to update a group, so it's not paginated
        // that way we have all users email addresses to map
        let limit = 100;
        let offset = page * limit;
        let expand = "User";
        let sort = "User.firstName ASC, User.lastName ASC";

        const groupAssignmentOptions = {
          limit: limit,
          sort: sort,
          filter: filter,
          offset: offset,
          expand: expand,
        };

        console.log(groupAssignmentOptions);

        const response = await GroupService.getGroupAssignmentV2(groupAssignmentOptions);

        let rows = response.result.rows.map((user) => {
          user.displayName = user.User ? user.User.firstName + " " + user.User.lastName : "";
          return user;
        });

        console.log("Got response from group assignment call ", rows);

        if (page == 0) {
          this.editingGroup.GroupAssignments = rows;
        } else {
          this.editingGroup.GroupAssignments = this.editingGroup.GroupAssignments.concat(rows);
        }

        if (Number(this.editingGroup.GroupAssignments.length) < Number(response.result.count)) {
          console.log("GETTING MORE GROUP ASSIGNMENTS");
          page = page + 1;
          this.getGroupAssignments(page, false);
        } else {
          this.editingGroup.loadingUsers = false;
        }
        // this.editingGroup.loadingUsers = false;
      } catch (error) {
        console.log(error);
        this.editingGroup.loadingUsers = false;
      }
    },
    editGroup(groupId) {
      console.log("Editing group " + groupId);
      if (typeof groupId == "object") {
        var group = groupId;
      } else {
        group = this.table.groups.find((x) => x.groupId === groupId);
      }

      this.editingGroup.loadingUsers = true;
      // console.log("clearing user table data");
      // Clear group assignments
      this.editingGroup.GroupAssignments = [];
      // Reset table data
      this.editingGroup.userTable.page = 1;
      // The page is not staying in sync with the page.sync value, reset it here
      this.editingGroup.userTable.options.page = 1;
      // TODO: Make sure it's ok to not reset items per page between table
      // this.editingGroup.userTable.itemsPerPage = 10;
      this.editingGroup.userTable.awaitingPage = 1;

      this.groupEditForm.showGroupAssignmentPage = false;
      // this.editingGroup.tablePage = 1;
      this.editingGroup.clientId = group.clientId;
      this.editingGroup.groupId = group.groupId;
      this.editingGroup.groupDisplayName = group.groupDisplayName;
      this.editingGroup.groupName = group.groupName;
      this.editingGroup.groupType = group.groupType;
      this.editingGroup.hierarchyRules = group.hierarchyRules;
      this.editingGroup.enrollmentRules = group.enrollmentRules;
      this.editingGroup.includeSubClients = group.includeSubClients;
      this.editingGroup.creatorName = null;

      // If it's a complex group, we get the rules and store them
      if (group.groupType == "Complex") {
        var ruleGroup = this.ruleGroups.find((x) => x.contextResourceId == group.groupId);

        this.editingGroup.rules = null;
        this.editingGroup.ruleName = null;
        this.editingGroup.ruleDescription = null;
        if (ruleGroup) {
          this.editingGroup.rules = ruleGroup.rules;
          this.editingGroup.ruleName = ruleGroup.name;
          this.editingGroup.ruleDescription = ruleGroup.description;
        }

        this.complexGroup.showSaveButton = false;
      } else {
        this.editingGroup.rules = null;
      }

      if (group.groupType === "Inform") {
        this.editingGroup.informQuery = group?.informQuery;
      }
      this.editingGroup.loadingGroupRefresh = false;
      this.showEditGroup = true;
      console.log(this.editingGroup);
      this.getGroupAssignments(0, true);
      this.getGroupCreator(group.groupId, group.createdBy);
    },
    async getGroupCreator(groupId, userId) {
      if (!userId) return console.log("No userId to get group creator");
      try {
        console.log({ groupId, userId });
        let filter = `status != 'Deleted' && userId='${userId}'`;

        const options = {
          limit: 1,
          filter,
        };

        const response = await UserService.getUsersV2(options);
        if (groupId !== this.editingGroup?.groupId) return;
        console.log("Group creator", response);
        const user = response?.result?.rows?.[0];
        if (!user) throw 404;
        this.editingGroup.creatorName = user.displayName || `${user.firstName} ${user.lastName}`;
        // this.editingGroup.loadingUsers = false;
      } catch (error) {
        console.log("Error getting group creator", error);
        // this.editingGroup.loadingUsers = false;
      }
    },
    clearEditGroupForm() {
      this.showEditGroup = false;
      this.showLoadingDialog = false;
      this.editingGroup.GroupAssignments = [];
      this.editingGroup.hierarchyRules = null;
      this.editingGroup.enrollmentRules = null;
      this.editingGroup.groupType = null;
      this.editingGroup.rules = null;
      this.editingGroup.ruleName = null;
      this.editingGroup.ruleDescription = null;
      this.editingGroup.creatorName = null;
      if (this.$refs.groupEditForm) this.$refs.groupEditForm.reset();
    },
    clearUserGroupForm() {
      this.groupEditForm.showGroupAssignmentPage = false;
      this.userTable.debounceSearch = null;
      this.userTable.search = null;
    },
    async deleteGroup() {
      this.showLoadingDialog = true;
      this.dialogDeleteMessage = false;
      this.loadingHeaderMessage = "Deleting group";
      this.loadingMessageLine1 = "This should just take a minute.";
      this.loadingMessageLine2 = "";
      try {
        let response = await GroupService.deleteGroups([this.editingGroup.groupId], this.clientId);
        console.log("Group deletion response ", response);
        this.getGroups(true);
        this.table.selected = [];
      } catch (err) {
        console.log("Error deleting groups ", err);
        // Now we want to show the delete error screen with an explanation
        if (err.data && err.data.length) {
          this.dialogDeleteErrorMessage = true;
          this.dialogDeleteErrorArray = err.data;
        }
      } finally {
        this.clearEditGroupForm();
      }
    },
    async deleteGroups() {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Deleting groups";
      this.loadingMessageLine1 = "This should just take a minute.";
      this.loadingMessageLine2 = "";
      var array = this.table.selected.map(function (group) {
        return group.groupId;
      });
      console.log(array);

      try {
        let response = await GroupService.deleteGroups(array, this.clientId);
        console.log("Group deletion response ", response);
        this.getGroups(true);
        this.table.selected = [];
        this.dialogDeleteMessage = false;
      } catch (err) {
        console.log("Error deleting groups ", err);
        // Now we want to show the delete error screen with an explanation
        if (err.data && err.data.length) {
          this.dialogDeleteErrorMessage = true;
          this.dialogDeleteErrorArray = err.data;
        }
      } finally {
        this.clearEditGroupForm();
      }
    },
    // Used by group deletion error message to display what group is tied to a program
    getGroupName(groupId) {
      let matchingGroup = this.table.groups.find((x) => x && x.groupId == groupId);
      if (matchingGroup) return matchingGroup.groupDisplayName;
      else return groupId;
    },
    handleEditGroupUpdate(data) {
      this.editingGroup.groupDisplayName = data.groupDisplayName;
      this.updateGroup();
    },
    async updateGroup() {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Updating Group";
      this.loadingMessageLine1 = "This shouldn't take but a minute.";
      this.loadingMessageLine2 = "";

      var group = {
        groupId: this.editingGroup.groupId,
        groupName: this.editingGroup.groupDisplayName,
        groupDisplayName: this.editingGroup.groupDisplayName,
        groupType: this.editingGroup.groupType,
        updatedBy: this.userProfile.updatedBy,
      };
      if (this.editingGroup.clientId !== null) {
        group.clientId = this.editingGroup.clientId;
      }
      if (this.editingGroup.hierarchyRules) group.hierarchyRules = this.editingGroup.hierarchyRules;
      console.log("Updating group", group);

      try {
        let response;
        if (group.groupType == "List") {
          if (!this.editingGroup.loadingUsers)
            group.userIds = this.editingGroup.GroupAssignments.map((x) => x.userId);
          response = await GroupService.createGroupV2(group, "list");
        } else {
          response = await GroupService.updateGroup(this.editingGroup.groupId, group);
        }
        console.log(response);
        this.clearEditGroupForm();
        this.getGroups(false, this.editingGroup.groupId);
      } catch (err) {
        console.log("Error updating group ", err);
      } finally {
        this.showLoadingDialog = false;
      }
    },
    deleteGroupAssignment(userId) {
      console.log("Deleting assignment " + userId);
      //Updates local array of group assignments
      var groupAssignmentIndex = this.editingGroup.GroupAssignments.findIndex(
        (x) => x.userId === userId
      );
      this.editingGroup.GroupAssignments.splice(groupAssignmentIndex, 1);

      // var groupIndex = this.table.groups.findIndex(
      //   x => x.groupId === this.editingGroup.groupId
      // );
      // console.log(groupIndex);
    },
    loadUserGroupAssignmentPage() {
      this.groupEditForm.loadingUserTable = true;
      this.groupEditForm.showGroupAssignmentPage = true;
      this.groupEditForm.loadingUserTable = false;
      // if (this.userTable.options) this.userTable.options.page = 1;
    },
    addUserToGroupAssignment(user) {
      console.log("Adding user: ", user);
      if (!this.editingGroup.GroupAssignments.find((x) => x.userId == user.userId)) {
        this.editingGroup.GroupAssignments.push({
          userId: user.userId,
          displayName: user.firstName + " " + user.lastName,
          User: user,
        });
      } else this.removeUserFromGroupAssignment(user.userId || user);
    },
    removeUserFromGroupAssignment(user) {
      console.log("Removing user: ", user);
      // this.editingGroup.otherUsers.push(user);
      var index = this.editingGroup.GroupAssignments.findIndex((x) => x.userId == user);
      this.editingGroup.GroupAssignments.splice(index, 1);
    },
    loadComplexGroupForm() {
      this.complexGroupForm.dialogCreator = true;
      this.complexGroupForm.ruleBuilderTrigger = !this.complexGroupForm.ruleBuilderTrigger;
      this.complexGroup.groupDisplayName = null;
      this.complexGroup.ruleName = null;
      this.complexGroup.ruleDescription = null;
      this.complexGroup.showSaveButton = true;
      this.complexGroup.rules = {
        name: null,
        description: null,
        rule: false,
        operator: "AND",
        id: 1,
        depth: 1,
        group: [],
        lastInGroup: true,
      };
    },
    closeRuleBuilder(rules) {
      console.log(rules);
      this.complexGroupForm.dialogRuleBuilder = false;
      this.complexGroup.showSaveButton = true;

      //If they clicked cancel, we don't overwrite rules
      // if (rules !== null) {
      this.complexGroup.rules = rules;
      // }
    },
    onRulesValueChanged(newVal) {
      if (
        !(
          JSON.stringify(newVal).includes('"group":[]') ||
          JSON.stringify(newVal).includes('"contextColumn":null') ||
          JSON.stringify(newVal).includes('"operator":null') ||
          JSON.stringify(newVal).includes('"value":null') ||
          JSON.stringify(newVal).includes('"value":""')
        )
      ) {
        console.log("Valid rule");
      } else {
        console.log("Invalid rule");
      }
      this.complexGroup.rules = newVal;
    },
    onNameValueChanged(newVal) {
      this.complexGroup.ruleName = newVal;
      this.complexGroup.groupDisplayName = newVal;
    },
    onDescriptionValueChanged(newVal) {
      this.complexGroup.ruleDescription = newVal;
    },
    resetComplexGroupForm() {
      if (this.$refs.complexGroupForm) this.$refs.complexGroupForm.reset();
      this.complexGroup.rules = null;
      this.complexGroupForm.dialogCreator = false;
    },
    handleComplexGroupCreate(data) {
      this.complexGroup.groupDisplayName = data.groupDisplayName;
      this.complexGroup.includeSubClients = data.includeSubClients;
      this.createComplexGroup();
    },
    createComplexGroup() {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Creating your complex group";
      this.loadingMessageLine1 = "This should just take a few seconds!";
      this.loadingMessageLine2 = "";
      var rules = this.complexGroup.rules;
      rules.name = this.complexGroup.groupDisplayName; // this.complexGroup.ruleName;
      rules.description = this.complexGroup.groupDisplayName; //this.complexGroup.ruleDescription;
      var obj = {
        clientId: this.clientId,
        groupName: this.complexGroup.groupDisplayName,
        groupDisplayName: this.complexGroup.groupDisplayName,
        includeSubClients: this.complexGroup.includeSubClients,
        rules: rules,
      };
      GroupService.createComplexGroup(obj)
        .then((response) => {
          console.log(response);
          this.showLoadingDialog = false;
          this.resetComplexGroupForm();
          this.getGroups(true);
        })
        .catch((error) => {
          console.log("Error!", error.response);
          this.showLoadingDialog = false;
        });
    },
    loadCommunicationWizard() {
      this.$router.push({
        name: "communicationwizard",
        query: {
          previousRoute: this.$route.name,
          group: 1,
        },
        params: {
          groupId: this.table.selected.map((x) => x.groupId || x),
        },
      });
    },
    async refreshClientId() {
      this.table.selected = [];
      // this.users = this.allUsers.filter((x) => x.clientId == this.clientId);
      // this.groups = this.allGroups.filter((x) => x.clientId == this.clientId);
      await this.getGroups(true);
      this.getLearningModules();
      this.getMetrics();
    },
    downloadTemplate() {
      //Downloads template file for list group uploads
      GroupService.downloadTemplate();
    },
    closeGroupCreator() {
      this.showCreateSimpleGroup = false;
      this.showCreateQrGroup = false;
      this.showGroupTypeSelector = false;
      // this.getGroupsV2(true);
    },
    refreshGroup(groupId, clientId) {
      this.editingGroup.loadingGroupRefresh = true;
      GroupService.refreshGroupsV2(groupId, clientId)
        .then((response) => {
          console.log("Group refresh response ", response);
          this.editingGroup.loadingGroupRefresh = false;
          this.getGroupAssignments(0, true);
        })
        .catch((error) => {
          console.log("Error!", error.response);
          this.editingGroup.loadingGroupRefresh = false;
        });
    },
    downloadGroup() {
      const group = this.editingGroup;
      const rows = (group.GroupAssignments || [])
        .filter((assignment) => assignment && assignment.User)
        .map((assignment) => ({
          name: `${assignment.User.firstName || ""} ${assignment.User.lastName || ""}`.trim(),
          contact: assignment.User.businessEmail || assignment.User.businessPhone || "",
        }));
      const columns = [
        { label: "Name", value: "name" },
        { label: "Email or Phone", value: "contact" },
      ];
      const csv = this.generateCSV(columns, rows);
      const baseName = group.groupDisplayName || group.groupName || `group-${group.groupId}`;
      const safeName = baseName.replace(/[\\/:*?"<>|]/g, "-").trim() || `group-${group.groupId}`;
      this.downloadCSV(csv, `${safeName}.csv`);
    },
    async handleRefreshInformGroup(groupId) {
      await this.refreshInformGroup(groupId);
      await this.getGroups(false);
    },
    async refreshInformGroup(groupId) {
      this.editingGroup.loadingGroupRefresh = true;
      try {
        await GroupService.refreshInformGroupV3({ groupId });
        await this.getGroupAssignments(0, true);
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Failed to refresh dynamic data group. Please try again later.",
        });
      } finally {
        this.editingGroup.loadingGroupRefresh = false;
      }
    },
    toggleGroupSelected(group) {
      const idx = this.table.selected.findIndex((x) => x.groupId == group.groupId);
      if (idx !== -1) this.table.selected.splice(idx, 1);
      else this.table.selected.push(group);
    },
    clearSelected() {
      this.table.selected = [];
    },
    refreshComplexGroupEstimate() {
      this.complexGroup.loadingEstimatedParticipants = true;
      const originalId = this.complexGroup.estimatedParticipantsResponseId;
      RulesService.estimateRuleGroup(
        [
          {
            context: "grouprules",
            audienceId: null,
            includeSubClients: this.complexGroup.includeSubClients,
          },
        ],
        this.complexGroup.rules,
        this.clientId
      )
        .then((rules) => {
          if (originalId == this.complexGroup.estimatedParticipantsResponseId)
            this.complexGroup.estimatedParticipants = rules.filter((x) => x.logic).length;
          this.complexGroup.loadingEstimatedParticipants = false;
          this.complexGroup.disableEstimatedParticipants = true;
          console.log("BEGINNING TIMEOUT");
          setTimeout(() => {
            this.complexGroup.disableEstimatedParticipants = false;
          }, 10000);
        })
        .catch((e) => {
          console.log("Error getting rule estimate", e);
          this.complexGroup.estimatedParticipants = null;
          this.complexGroup.loadingEstimatedParticipants = false;
          this.complexGroup.disableEstimatedParticipants = true;
          setTimeout(() => {
            this.complexGroup.disableEstimatedParticipants = false;
          }, 5000);
        });
    },
    async loadQrCode(groupId) {
      const clientId = Number(
        this.clientId !== undefined && this.clientId !== null
          ? this.clientId
          : this?.userProfile?.clientId
      );
      this.qr = {
        dialog: true,
        error: null,
        url: null,
        groupId,
        clientId,
      };
    },
    showQrCode(type, clientId) {
      let evaluate = false;
      if (type == "List") {
        const client =
          clientId == this.userProfile?.clientId
            ? this.userProfile?.Client
            : this.clients.find((x) => x.clientId == clientId);
        if (client?.onboardingTokenPersistent) evaluate = true;
      }
      return evaluate;
    },
    handleSearchValueChange(val) {
      this.table.search = val;
      this.getGroups(true);
    },
    handlePeoplePageClick() {
      this.$router.push("/people");
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "postItArray", "permissions", "globalClientId"]),
    ...mapGetters(["hasPermissionToViewClients", "hasPermissionToManageClients"]),
    canOpenGroupDrawer() {
      return this.canCreateGroup || this.hasPermissionToViewClients();
    },
    readonlyGroupDrawer() {
      return this.canOpenGroupDrawer && !this.canCreateGroup;
    },
    canCreateGroup() {
      return this.permissions?.includes("groups:create:group");
    },
    canManageInformGroups() {
      return (
        this.canCreateGroup &&
        this.permissions.includes("inform:read:schema") &&
        !this.isMobile &&
        this.clientHasInformTenant
      );
    },
    clientId: {
      get: function () {
        return this.globalClientId;
      },
      set: function (newVal) {
        this.$store.dispatch("setClientId", newVal);
      },
    },
    visibleGroups() {
      var startingIndex = this.table.options.page - 1 || 0;
      let itemsPerPage =
        (this.table.options && this.table.options.itemsPerPage) || this.table.itemsPerPage;
      // return this.table.groups;
      return this.table.groups
        .slice()
        .slice(startingIndex * itemsPerPage, startingIndex * itemsPerPage + itemsPerPage)
        .filter((x) => x !== undefined);
    },
    validateRules() {
      if (this.complexGroup.rules !== null) {
        if (
          !(
            JSON.stringify(this.complexGroup.rules).includes('"group":[]') ||
            JSON.stringify(this.complexGroup.rules).includes('"contextColumn":null') ||
            JSON.stringify(this.complexGroup.rules).includes('"operator":null') ||
            JSON.stringify(this.complexGroup.rules).includes('"value":null') ||
            JSON.stringify(this.complexGroup.rules).includes('"value":""') ||
            !this.complexGroup.groupDisplayName
          )
        ) {
          return true;
        }
      }
      return false;
    },
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    groupColumns() {
      //remove the fields we don't need, to declutter mobile
      //in this case, group type and rules
      return this.isMobile
        ? this.columns.filter((x) => x.value != "enrollmentRules" && x.value != "groupType")
        : this.columns;
    },
  },
  watch: {
    "complexGroup.rules": {
      handler() {
        this.complexGroup.estimatedParticipants = null;
        this.complexGroup.estimatedParticipantsResponseId = Date.now();
      },
      deep: true,
    },
    "userTable.debounceSearch": debounce(function (newVal) {
      this.userTable.search = newVal;
      // this.getUsersV2(true);
    }, 500),
    "table.options": {
      handler(newVal, oldVal) {
        console.log("New options ", newVal);
        console.log("Old options ", oldVal);
        // Used to indicate if we wipe the table and start with an empty array, versus pushing more data on
        var reset = false;
        var apiCall = true;
        // If none of these exist then we're probably loading the page for the first time so we reset
        if (!oldVal.itemsPerPage || !oldVal.page || !oldVal.sortBy || !oldVal.sortDesc)
          reset = true;
        else {
          // If any values changed from their old to new states, then we reset because they're changing a sort
          if (
            newVal.itemsPerPage !== oldVal.itemsPerPage ||
            (newVal.sortBy && oldVal.sortBy && newVal.sortBy[0] !== oldVal.sortBy[0]) ||
            (newVal.sortDesc && oldVal.sortDesc && newVal.sortDesc[0] !== oldVal.sortDesc[0])
          )
            reset = true;
        }
        // This means that they just hit the next button. We have to check what the max page we've loaded in is
        // We can optimize and load in existing data
        if (
          !reset &&
          oldVal.page !== newVal.page &&
          this.table.groups.filter((x) => x !== undefined).length >
            (newVal.page - 1) * newVal.itemsPerPage
        ) {
          console.log("We're not going to make the API call");
          apiCall = false;
        }
        if (apiCall) this.getGroups(reset);
      },
      deep: true,
    },
    clientId: {
      async handler(newVal) {
        console.log("New clientId", newVal);
        this.table.selected = [];
        // this.users = this.allUsers.filter((x) => x.clientId == this.clientId);
        // this.groups = this.allGroups.filter((x) => x.clientId == this.clientId);
        await this.getGroups(true);
        this.getLearningModules();
        this.getMetrics();
      },
    },
  },
};
</script>

<style scoped>
/* Divider because v-divider doesn't appear when flex-direction is column */
.divider {
  border: 0.5px solid lightgray;
}
</style>
